/**
 * Fiber and Buffer Color Utilities
 * Handles color naming for fibers and buffers in optical cables
 */

export interface FiberColor {
  name: string;
  color: string; // Hex color for UI display
}

// Standard fiber colors (1-12)
export const FIBER_COLORS: FiberColor[] = [
  { name: 'Modrá', color: '#0000FF' },
  { name: 'Oranžová', color: '#FF8C00' },
  { name: 'Zelená', color: '#00FF00' },
  { name: 'Hnedá', color: '#8B4513' },
  { name: 'Šedá', color: '#808080' },
  { name: 'Biela', color: '#FFFFFF' },
  { name: 'Červená', color: '#FF0000' },
  { name: 'Čierna', color: '#000000' },
  { name: 'Žltá', color: '#FFFF00' },
  { name: 'Fialová', color: '#800080' },
  { name: 'Ružová', color: '#FFC0CB' },
  { name: 'Tyrkysová', color: '#40E0D0' },
];

/**
 * Get buffer color by index (1-based)
 * Buffers use the same colors as fibers
 */
export const getBufferColor = (bufferIndex: number): FiberColor => {
  const index = ((bufferIndex - 1) % FIBER_COLORS.length);
  return FIBER_COLORS[index];
};

/**
 * Get fiber color name within a buffer
 * @param fiberIndexInBuffer - Fiber index within the buffer (1-12 or 1-24)
 * @param bufferSize - Size of the buffer (12 or 24)
 * @returns Color name (e.g., "Modrá", "Modro-čierna", "Čierno-priesvitná")
 */
export const getFiberColorInBuffer = (fiberIndexInBuffer: number, bufferSize: 12 | 24 = 12): string => {
  if (bufferSize === 12) {
    // For 12-fiber buffer, use standard colors
    const index = ((fiberIndexInBuffer - 1) % FIBER_COLORS.length);
    return FIBER_COLORS[index].name;
  } else {
    // For 24-fiber buffer
    if (fiberIndexInBuffer <= 12) {
      // First 12 fibers: standard colors
      const index = ((fiberIndexInBuffer - 1) % FIBER_COLORS.length);
      return FIBER_COLORS[index].name;
    } else {
      // Fibers 13-24: color with black stripe
      const baseIndex = ((fiberIndexInBuffer - 13) % FIBER_COLORS.length);
      const baseColor = FIBER_COLORS[baseIndex].name;
      
      // Special case: fiber 20 (index 8 in second set) is "Čierno-priesvitná"
      if (fiberIndexInBuffer === 20) {
        return 'Čierno-priesvitná';
      }
      
      return `${baseColor.replace('á', 'o')}-čierna`;
    }
  }
};

/**
 * Get fiber color name for a global fiber number in a cable
 * @param fiberNumber - Global fiber number in the cable (1-based)
 * @param bufferConfig - Buffer configuration
 * @returns Object with buffer info and color name
 */
export const getFiberColorName = (
  fiberNumber: number,
  bufferConfig: { buffer_size: 12 | 24; buffers: { index: number; color: string }[] }
): { bufferIndex: number; bufferColor: string; fiberInBuffer: number; colorName: string } => {
  const { buffer_size, buffers } = bufferConfig;
  
  // Calculate which buffer this fiber belongs to
  const bufferIndex = Math.ceil(fiberNumber / buffer_size);
  const fiberInBuffer = ((fiberNumber - 1) % buffer_size) + 1;
  
  // Get buffer color
  const buffer = buffers.find(b => b.index === bufferIndex);
  const bufferColorName = buffer?.color || getBufferColor(bufferIndex).name;
  
  // Get fiber color within buffer
  const fiberColorName = getFiberColorInBuffer(fiberInBuffer, buffer_size);
  
  return {
    bufferIndex,
    bufferColor: bufferColorName,
    fiberInBuffer,
    colorName: fiberColorName,
  };
};

/**
 * Popis vlákna do zoznamu, napr. „(Modrý) Vlákno 3 (Zelená)".
 *
 * Číslo bufra sa zámerne nepíše — technik v kazete rozoznáva bufre podľa FARBY,
 * nie podľa poradia, a „Bufer 1, " na začiatku každej položky len predlžovalo
 * riadok a tlačilo dôležité údaje mimo obrazovku.
 */
export const getFiberDisplayName = (
  fiberNumber: number,
  bufferConfig: { buffer_size: 12 | 24; buffers: { index: number; color: string }[] }
): string => {
  const info = getFiberColorName(fiberNumber, bufferConfig);
  return `(${info.bufferColor}) Vlákno ${info.fiberInBuffer} (${info.colorName})`;
};

/**
 * Calculate buffer configuration based on fiber count
 * @param fiberCount - Total number of fibers
 * @param bufferSize - Size of each buffer (12 or 24)
 * @returns Buffer configuration
 */
export const calculateBufferConfig = (
  fiberCount: number,
  bufferSize: 12 | 24 = 12
): { buffer_size: 12 | 24; buffers: { index: number; color: string }[] } => {
  const bufferCount = Math.ceil(fiberCount / bufferSize);
  const buffers = Array.from({ length: bufferCount }, (_, i) => ({
    index: i + 1,
    color: getBufferColor(i + 1).name,
  }));
  
  return {
    buffer_size: bufferSize,
    buffers,
  };
};

/**
 * Get available buffer size options for a given fiber count
 */
export const getAvailableBufferSizes = (fiberCount: number): Array<{ size: 12 | 24; label: string }> => {
  const options: Array<{ size: 12 | 24; label: string }> = [];
  
  // 12-fiber buffers are always available
  const count12 = Math.ceil(fiberCount / 12);
  options.push({ size: 12, label: `${count12}× 12-vláknový bufer` });
  
  // 24-fiber buffers only if fiber count is divisible by 24 or >= 24
  if (fiberCount >= 24) {
    const count24 = Math.ceil(fiberCount / 24);
    options.push({ size: 24, label: `${count24}× 24-vláknový bufer` });
  }
  
  return options;
};


/**
 * Bežné počty vlákien optických káblov.
 *
 * Doplnené 2026-09-30. Predtým ponúkal panel „Kábel klikaním" len 12/24/48/96
 * a v dialógu úpravy kábla sa počet vlákien nedal zmeniť vôbec.
 * Okrem tohto zoznamu sa dá zadať aj vlastné číslo (1 až MAX_FIBER_COUNT).
 */
export const STANDARD_FIBER_COUNTS = [1, 2, 4, 8, 12, 24, 36, 48, 72, 96, 144];

/** Horná hranica — musí sedieť s MAX_FIBERS v backend/app/utils/fibers.py */
export const MAX_FIBER_COUNT = 144;

/** Počet vlákien -> hodnota stĺpca cable_type ("24-fiber") */
export const cableTypeFromFiberCount = (fiberCount: number): string =>
  `${fiberCount}-fiber`;

/** Opačný smer: "24-fiber" -> 24. Neznámy tvar vráti 12 (staré káble). */
export const fiberCountFromCableType = (cableType?: string | null): number => {
  if (!cableType) return 12;
  const m = String(cableType).match(/(\d+)/);
  if (!m) return 12;
  const n = parseInt(m[1], 10);
  return n >= 1 && n <= MAX_FIBER_COUNT ? n : 12;
};
